import type { MaybeRefOrGetter } from "vue";
import type { LimitableResource } from "@/schemas/subscription-limits.schema";
import { computed, toValue } from "vue";
import { useUserProfile } from "@/stores/user-profile.store";

/**
 * Mapping from resource name to the corresponding limit field in subscription.limits
 */
const RESOURCE_TO_LIMIT_FIELD: Record<string, string> = {
  // Org-level resources
  projects: "max_projects",
  administrators: "max_administrators",
  // Per-project resources
  agents_per_project: "max_agents_per_project",
  webhooks_per_project: "max_webhooks_per_project",
  knowledge_per_project: "max_knowledge_per_project",
  analyses_per_project: "max_structured_analyses_per_project",
  team_per_project: "max_team_per_project",
  api_keys_per_user_per_project: "max_api_keys_per_user_per_project",
};

/**
 * Composable hook for checking subscription resource limits
 *
 * Derives limits from the user profile store (loaded via /me) and
 * current counts from the caller (typically list query .length).
 * This makes the check fully reactive without any API calls.
 *
 * @param resource - The resource type to check (can be reactive)
 * @param count - Current count of the resource (can be reactive). undefined = still loading.
 *
 * @example
 * ```ts
 * // Per-project check with list query length
 * const agentCount = computed(() => agentsQuery.data.value?.length);
 * const { isAtLimit, limitInfo } = useResourceLimit("agents_per_project", agentCount);
 *
 * // Org-level check
 * const projectCount = computed(() => projectsQuery.data.value?.length);
 * const { isAtLimit } = useResourceLimit("projects", projectCount);
 * ```
 */
export function useResourceLimit(
  resource: MaybeRefOrGetter<LimitableResource>,
  count: MaybeRefOrGetter<number | undefined>,
) {
  const userProfile = useUserProfile();

  const limit = computed(() => {
    const res = toValue(resource);
    const fieldName = RESOURCE_TO_LIMIT_FIELD[res];
    if (!fieldName) return null;
    const limits = userProfile.user?.subscription?.limits;
    if (!limits) return null;
    return (limits as Record<string, number | null>)[fieldName] ?? null;
  });

  const current = computed(() => toValue(count) ?? 0);

  const isAtLimit = computed(() => {
    if (limit.value === null) return false; // unlimited
    return current.value >= limit.value;
  });

  const isOverLimit = computed(() => {
    if (limit.value === null) return false;
    return current.value > limit.value;
  });

  const remaining = computed(() => {
    if (limit.value === null) return null;
    return Math.max(0, limit.value - current.value);
  });

  return {
    /** Loading state - true when count is not yet available */
    isLoading: computed(() => toValue(count) === undefined),
    /** Full limit info including current, limit, remaining */
    limitInfo: computed(() => ({
      current: current.value,
      limit: limit.value,
      remaining: remaining.value,
    })),
    /** True if current >= limit (cannot create more) */
    isAtLimit,
    /** True if current > limit (grandfathered over limit) */
    isOverLimit,
  };
}
